import { useParams, Link, useNavigate } from "react-router-dom";
import { useOne, useList } from "@refinedev/core";
import { XCircle } from "lucide-react";
import { PageHeader } from "@/components/forms/PageHeader";
import { MoneyDisplay } from "@/components/forms/MoneyDisplay";
import { formatDate } from "@/lib/formatters";

export function VentaDetallePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: venta } = useOne({
    resource: "ventas",
    id,
    meta: { select: "*, vendedores(nombre), tiendas(nombre)" },
  });
  const { data: pagos } = useList({
    resource: "venta_pagos",
    filters: id ? [{ field: "venta_id", operator: "eq", value: id }] : [],
    meta: { select: "*, financieras(nombre)" },
  });

  if (!venta?.data) return <div className="card p-8 text-center">Cargando...</div>;
  const v = venta.data;

  return (
    <div className="max-w-2xl space-y-4">
      <PageHeader
        title={`Venta #${v.numero_consecutivo}`}
        subtitle={`${formatDate(v.fecha_venta)} · ${(v.tiendas as { nombre: string })?.nombre}`}
        actions={
          v.estado === "confirmada" && (
            <button
              onClick={() => navigate(`/ventas/${id}/anular`)}
              className="btn-secondary inline-flex items-center gap-2 text-error"
            >
              <XCircle className="size-4" /> Anular
            </button>
          )
        }
      />

      {v.estado === "anulada" && (
        <div className="card p-3 bg-error/10 border-error text-error text-sm">
          Venta anulada. Motivo: {v.motivo_anulacion ?? "—"}
        </div>
      )}

      <section className="card p-4 space-y-1">
        <h2 className="text-sm font-semibold text-fg-secondary">Cliente</h2>
        <p className="font-medium">{v.cliente_nombre}</p>
        <p className="text-sm text-fg-muted">
          {v.cliente_documento}
          {v.cliente_telefono && ` · ${v.cliente_telefono}`}
          {v.cliente_ciudad && ` · ${v.cliente_ciudad}`}
        </p>
      </section>

      <section className="card p-4 space-y-1">
        <h2 className="text-sm font-semibold text-fg-secondary">
          {v.tipo_producto === "celular" ? "Celular" : "Moto"}
        </h2>
        <p className="font-medium">
          {v.producto_marca} {v.producto_modelo}
        </p>
        <p className="text-sm text-fg-muted">
          {v.producto_color && `${v.producto_color} · `}
          <MoneyDisplay value={v.valor_total} />
        </p>
        {v.tipo_producto === "moto" ? (
          <ul className="text-sm text-fg-muted pt-2 space-y-0.5 font-mono">
            {v.vin_chasis && <li>VIN: {v.vin_chasis}</li>}
            {v.placa && <li>Placa: {v.placa}</li>}
            {v.serial_motor && <li>Motor: {v.serial_motor}</li>}
            {v.serial_bateria && <li>Batería: {v.serial_bateria}</li>}
          </ul>
        ) : (
          <ul className="text-sm text-fg-muted pt-2 space-y-0.5 font-mono">
            {v.imei_1 && <li>IMEI: {v.imei_1}</li>}
            {v.imei_2 && <li>IMEI 2: {v.imei_2}</li>}
            {v.numero_serie && <li>Serie: {v.numero_serie}</li>}
            {v.producto_almacenamiento && <li>{v.producto_almacenamiento}</li>}
          </ul>
        )}
      </section>

      <section className="card p-4">
        <h2 className="text-sm font-semibold text-fg-secondary mb-2">Pagos</h2>
        <ul className="space-y-2">
          {pagos?.data.map((p) => (
            <li key={p.id} className="flex items-center justify-between text-sm">
              <span>
                {p.tipo === "efectivo"
                  ? "💵 Efectivo"
                  : `💳 ${(p.financieras as { nombre: string })?.nombre}`}
                {p.numero_aprobacion && ` · ${p.numero_aprobacion}`}
              </span>
              <MoneyDisplay value={p.monto as number} className="font-medium" />
            </li>
          ))}
        </ul>
      </section>

      {v.comentarios && (
        <section className="card p-4">
          <h2 className="text-sm font-semibold text-fg-secondary mb-1">Comentarios</h2>
          <p className="text-sm">{v.comentarios}</p>
        </section>
      )}

      <Link to="/ventas" className="btn-secondary inline-block">Volver</Link>
    </div>
  );
}
